import { z } from "zod";

// Define time of day options
export const timeOptions = [
  { value: "morning", label: "Morning", icon: "Sun" },
  { value: "noon", label: "Noon", icon: "Sun" },
  { value: "afternoon", label: "Afternoon", icon: "Clock" },
  { value: "evening", label: "Evening", icon: "Clock" },
  { value: "bedtime", label: "Bedtime", icon: "Moon" }
];

// Define frequency options
export const frequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "twice-daily", label: "Twice Daily" },
  { value: "three-times-daily", label: "Three Times Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Twice a Week" },
  { value: "monthly", label: "Monthly" },
  { value: "as-needed", label: "As Needed" }
];

// Define food instruction options
export const foodInstructionOptions = [
  { value: "", label: "Any time" },
  { value: "before-meals", label: "Before meals" },
  { value: "with-meals", label: "With meals" },
  { value: "after-meals", label: "After meals" }
];

// Core medication form data type
export interface MedicationFormData {
  // Step 1: Basic details
  name: string;
  dosage: string;
  form: string;
  
  // Step 2: Schedule
  frequency: string;
  timeOfDay: string[];
  dosesAtTime: Record<string, number>;
  
  // Step 3: Cycle
  hasCycle: boolean;
  cycleWeeksOn: number;
  cycleWeeksOff: number;
  cycleStartDate?: string;
  cycleEndDate?: string;
  
  // Step 4: Additional Options
  foodInstructions: string;
  color?: string;
  notes?: string;
  refillTracking?: boolean;
  refillAmount?: number;
  refillThreshold?: number;
  refillsRemaining?: number;
}

// Validation schema for medication form
export const medicationFormSchema = z.object({
  name: z.string().min(1, "Medication name is required"),
  dosage: z.string().min(1, "Dosage is required"),
  form: z.string().min(1, "Medication form is required"),
  frequency: z.string().min(1, "Frequency is required"),
  timeOfDay: z.array(z.string()).min(1, "At least one time of day must be selected"),
  dosesAtTime: z.record(z.string(), z.number()),
  hasCycle: z.boolean(),
  cycleWeeksOn: z.number().min(1, "Weeks on must be at least 1"),
  cycleWeeksOff: z.number().min(1, "Weeks off must be at least 1"),
  cycleStartDate: z.string().optional(),
  cycleEndDate: z.string().optional(),
  foodInstructions: z.string(),
  color: z.string().optional(),
  notes: z.string().optional(),
  refillTracking: z.boolean().optional(),
  refillAmount: z.number().optional(),
  refillThreshold: z.number().optional(),
  refillsRemaining: z.number().optional()
});

// Type for validation function
export type StepValidationFn = (formData: MedicationFormData) => boolean;

// Types for the dialog component props
export interface AddMedicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddMedication?: (medication: MedicationFormData) => void;
  editMedication?: MedicationFormData;
}

// Type for medication summary (used for display after submission)
export interface MedicationSummary {
  id: string;
  name: string;
  dosage: string;
  form: string;
  frequency: string;
  timeOfDay: string[];
  dosesAtTime: Record<string, number>;
  hasCycle: boolean;
  cycleWeeksOn?: number;
  cycleWeeksOff?: number;
  foodInstructions?: string;
  color?: string;
}
